import type { AttendanceEmployeeRow, AttendanceSortKey } from "./attendance-types";

export type AttendanceSortDirection = "asc" | "desc";

export function filterEmployeesByName(
  rows: AttendanceEmployeeRow[],
  query: string
): AttendanceEmployeeRow[] {
  const q = query.trim().toLowerCase();
  if (!q) return rows;
  return rows.filter((row) => {
    const name = row.name?.toLowerCase() ?? "";
    const id = String(row.employeeId);
    return name.includes(q) || id.includes(q);
  });
}

function compareName(
  a: AttendanceEmployeeRow,
  b: AttendanceEmployeeRow,
  dir: AttendanceSortDirection
) {
  const cmp = a.name.localeCompare(b.name);
  return dir === "asc" ? cmp : -cmp;
}

export function sortAttendanceRows(
  rows: AttendanceEmployeeRow[],
  sortKey: AttendanceSortKey,
  nameDirection: AttendanceSortDirection = "asc"
): AttendanceEmployeeRow[] {
  const copy = [...rows];
  switch (sortKey) {
    case "absent":
      return copy.sort(
        (a, b) =>
          b.summary.absentDays - a.summary.absentDays ||
          b.summary.incompleteDays - a.summary.incompleteDays ||
          compareName(a, b, "asc")
      );
    case "incomplete":
      return copy.sort(
        (a, b) =>
          b.summary.incompleteDays - a.summary.incompleteDays ||
          b.summary.absentDays - a.summary.absentDays ||
          compareName(a, b, "asc")
      );
    case "hours":
      return copy.sort(
        (a, b) => a.summary.totalHours - b.summary.totalHours || compareName(a, b, "asc")
      );
    case "name":
    default:
      return copy.sort((a, b) => compareName(a, b, nameDirection));
  }
}

export function filterAndSortAttendanceRows(
  rows: AttendanceEmployeeRow[],
  nameQuery: string,
  sortKey: AttendanceSortKey,
  nameDirection: AttendanceSortDirection = "asc"
): AttendanceEmployeeRow[] {
  return sortAttendanceRows(filterEmployeesByName(rows, nameQuery), sortKey, nameDirection);
}

/** Next name direction when activating / toggling the Staff column header. */
export function nextNameSortDirection(
  sortKey: AttendanceSortKey,
  nameDirection: AttendanceSortDirection
): AttendanceSortDirection {
  if (sortKey !== "name") return "asc";
  return nameDirection === "asc" ? "desc" : "asc";
}
